import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime, fmtDuration, fmtNum } from '@/lib/format';
import type { Snapshot, SnapshotItem } from '@sklad-sync/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

export function SnapshotDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [items, setItems] = useState<SnapshotItem[] | null>(null);
  const [onlyMismatch, setOnlyMismatch] = useState(true);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(
    null,
  );

  const loadItems = useCallback(async () => {
    if (!id) return;
    const params = new URLSearchParams();
    if (onlyMismatch) params.set('mismatch', 'true');
    if (q.trim()) params.set('q', q.trim());
    try {
      const data = await apiFetch<{ items: SnapshotItem[] }>(
        `/api/snapshots/${id}/items?${params.toString()}`,
      );
      setItems(data.items);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání položek');
    }
  }, [id, onlyMismatch, q]);

  // Počet synchronizovatelných položek v aktuálně načteném seznamu. Ve výchozím
  // pohledu (jen nesrovnalosti, bez hledání) odpovídá celému snapshotu.
  const syncableCount = items?.filter(isSyncable).length ?? 0;

  // Hromadně zapíše korekce VŠECH synchronizovatelných položek snapshotu do
  // ostrého e-shopu (backend ignoruje filtry z UI). Zápis je nevratný.
  async function doSyncAll() {
    if (!id) return;
    if (
      !confirm(
        'Zapsat korekce všech synchronizovatelných položek tohoto snapshotu do OSTRÉHO e-shopu?\n\n' +
          'Filtry a hledání se ignorují — projede se celý snapshot. Akci nelze vzít zpět.',
      )
    ) {
      return;
    }
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const r = await apiFetch<{ total: number; synced: number; failed_count: number }>(
        `/api/snapshots/${id}/sync-all`,
        { method: 'POST' },
      );
      if (r.total === 0) {
        setBulkMsg({ kind: 'ok', text: 'Nic k synchronizaci — vše už je v synchronu.' });
      } else if (r.failed_count > 0) {
        setBulkMsg({
          kind: 'warn',
          text: `Synchronizováno ${r.synced}/${r.total} položek. Nepodařilo se: ${r.failed_count}.`,
        });
      } else {
        setBulkMsg({
          kind: 'ok',
          text: `Hotovo — synchronizováno ${r.synced} položek do e-shopu.`,
        });
      }
      await loadItems();
    } catch (e: unknown) {
      setBulkMsg({
        kind: 'err',
        text: e instanceof ApiError ? e.describe() : 'Hromadná synchronizace selhala',
      });
    } finally {
      setBulkBusy(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    apiFetch<{ snapshot: Snapshot }>(`/api/snapshots/${id}`)
      .then((d) => setSnapshot(d.snapshot))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Chyba'));
  }, [id]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  return (
    <>
      <div className="mb-4">
        <Link to="/sklad-sync" className="text-sm text-primary hover:text-primary-dark font-medium">
          ← Zpět na dashboard
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Snapshot #{id}</h1>
        {snapshot && (
          <p className="text-sm text-text-secondary mt-0.5">
            {fmtDateTime(snapshot.run_at)} · trvání {fmtDuration(snapshot.duration_ms)}
          </p>
        )}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {snapshot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Status" value={snapshot.status} accent={statusAccent(snapshot.status)} />
          <StatCard label="Varianty celkem" value={fmtNum(snapshot.variants_total)} />
          <StatCard
            label="Nesrovnalosti"
            value={fmtNum(snapshot.mismatch_count)}
            accent={snapshot.mismatch_count > 0 ? 'text-danger' : 'text-success'}
          />
          <StatCard
            label="Zdroje OK"
            value={`${snapshot.shoptet_ok + snapshot.brani_ok + snapshot.vyroba_ok}/3`}
          />
        </div>
      )}

      {snapshot?.error_message && (
        <div className="text-sm text-warning bg-warning-light border border-warning/30 rounded-lg px-4 py-3 mb-4">
          <strong>Hlášení chyb:</strong> {snapshot.error_message}
        </div>
      )}

      {(syncableCount > 0 || bulkMsg) && (
        <div className="mb-6">
          {syncableCount > 0 && (
            <button
              type="button"
              onClick={doSyncAll}
              disabled={bulkBusy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-action px-6 py-4 text-base font-bold text-white shadow-card transition-colors hover:bg-action-dark disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {bulkBusy ? (
                'Zapisuji do e-shopu…'
              ) : (
                <>
                  Synchronizovat vše do e-shopu
                  <span className="rounded-full bg-white/25 px-2 py-0.5 text-sm font-semibold">
                    {syncableCount}
                  </span>
                </>
              )}
            </button>
          )}
          {bulkMsg && (
            <div
              className={`mt-2 rounded-lg border px-4 py-2.5 text-sm ${
                bulkMsg.kind === 'ok'
                  ? 'border-success/30 bg-success-light text-success'
                  : bulkMsg.kind === 'warn'
                    ? 'border-warning/30 bg-warning-light text-warning'
                    : 'border-danger/30 bg-danger-light text-danger'
              }`}
            >
              {bulkMsg.text}
            </div>
          )}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card">
        <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-4 rounded-t-xl">
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={onlyMismatch}
              onChange={(e) => setOnlyMismatch(e.target.checked)}
            />
            Pouze nesrovnalosti
          </label>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat (kód, název, velikost)…"
            className="flex-1 min-w-[200px] text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg">
                <Th>Produkt</Th>
                <Th>Varianta</Th>
                <Th className="text-right" title="Skladovost zobrazená v e-shopu">
                  Shoptet
                </Th>
                <Th className="text-right" title="Fyzicky ve skladovém ERP (kzo11)">
                  Sklad
                </Th>
                <Th
                  className="text-right"
                  title="Skladový nárok — objednávky čekající na zpracování, CZ + SK dohromady"
                >
                  Skl. nárok
                </Th>
                <Th className="text-right" title="Zadání do výroby, ještě nenaskladněné">
                  Výroba
                </Th>
                <Th
                  className="text-right"
                  title="Sklad + Výroba − Skl. nárok = kolik má e-shop ukazovat"
                >
                  Očekávané
                </Th>
                <Th
                  className="text-right"
                  title="Shoptet − Očekávané. 0 = v synchronu, jinak nesrovnalost"
                >
                  Diff
                </Th>
                <Th title="Co je třeba udělat, aby Shoptet odpovídal realitě">Akce</Th>
              </tr>
            </thead>
            <tbody>
              {items == null ? (
                <tr>
                  <td colSpan={9} className="px-5 py-8 text-center text-text-muted">
                    Načítám…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-8 text-center text-text-muted">
                    Žádné položky.
                  </td>
                </tr>
              ) : (
                items.map((it) => {
                  const excluded = !!it.is_excluded;
                  return (
                    <tr
                      key={it.id}
                      className={`border-b border-border-light hover:bg-surface-hover transition-colors ${
                        excluded ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="px-0 py-0 text-text">
                        <a
                          href={`https://www.v-mart.cz/admin/produkty/?f%5BchosenPricelistId%5D=1&f%5Bcode%5D=${encodeURIComponent(it.product_code)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Otevřít v Shoptet administraci (přesný filter na kód produktu)"
                          className="flex items-center gap-3 px-5 py-2.5 hover:bg-primary-50 transition-colors cursor-pointer"
                        >
                          {it.image_url ? (
                            <img
                              src={it.image_url}
                              alt=""
                              loading="lazy"
                              className="w-10 h-10 rounded object-cover bg-bg flex-shrink-0 border border-border"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-bg border border-border-light flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-base font-semibold flex items-center gap-2">
                              {it.product_name}
                              {excluded && (
                                <span
                                  className="text-[10px] font-semibold text-text-muted bg-bg border border-border px-1.5 py-0.5 rounded"
                                  title="Tento produkt je vyřazen z reconciliace pravidlem v Administraci"
                                >
                                  vyřazeno
                                </span>
                              )}
                            </div>
                            <div className="text-sm font-mono font-semibold text-text mt-0.5">
                              {it.product_code}
                              {it.variant_name && `/${it.variant_name}`}
                            </div>
                          </div>
                        </a>
                      </td>
                      <td className="px-5 py-2.5 text-text">
                        {it.variant_name ? (
                          <span className="font-semibold text-base">{it.variant_name}</span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-right text-text">{fmtNum(it.shoptet_qty)}</td>
                      <td className="px-5 py-2.5 text-right text-text">{fmtNum(it.brani_qty)}</td>
                      <td className="px-5 py-2.5 text-right text-text">
                        {fmtNum(it.reservations_qty)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-text">{fmtNum(it.vyroba_qty)}</td>
                      <td className="px-5 py-2.5 text-right text-text-secondary">
                        {fmtNum(it.expected_qty)}
                      </td>
                      <td
                        className={`px-5 py-2.5 text-right text-base font-bold ${
                          excluded
                            ? 'text-text-muted'
                            : it.diff === 0
                              ? 'text-success'
                              : it.diff > 0
                                ? 'text-warning'
                                : 'text-danger'
                        }`}
                      >
                        {it.diff > 0 ? '+' : ''}
                        {it.diff}
                      </td>
                      <td className="px-5 py-2.5 text-text text-sm">
                        <ActionCell item={it} snapshotId={id ?? ''} onSynced={loadItems} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Th({
  children,
  className = '',
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <th
      title={title}
      className={`sticky top-0 z-10 bg-bg text-left text-xs font-semibold text-text-muted uppercase tracking-wider px-5 py-3 shadow-[0_1px_0_var(--color-border)] ${className}`}
    >
      {children}
    </th>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div className="bg-surface rounded-xl p-5 shadow-card">
      <div className="text-xs font-medium text-text-muted uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-2 ${accent ?? 'text-text'}`}>{value}</div>
    </div>
  );
}

function statusAccent(s: 'success' | 'partial' | 'failed'): string {
  return s === 'success' ? 'text-success' : s === 'partial' ? 'text-warning' : 'text-danger';
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// Lze položku srovnat zápisem do Shoptetu? Musí být nevyřazená, ještě
// nesynchronizovaná a akce „přidat" (diff<0) nebo „odebrat" (diff>0 & shoptet>0).
// „Zařadit do výroby" (diff>0 & shoptet=0) sem NEpatří. Musí sedět s WHERE
// v backend endpointu /sync-all.
function isSyncable(it: SnapshotItem): boolean {
  if (it.is_excluded || it.synced_at) return false;
  return it.diff < 0 || (it.diff > 0 && (it.shoptet_qty ?? 0) > 0);
}

// Akce: co je třeba udělat, aby Shoptet odpovídal očekávanému stavu.
// diff = shoptet − očekávané
//   diff < 0 → Shoptet ukazuje míň → přidat |diff| ks
//   diff > 0 → Shoptet ukazuje víc → odebrat diff ks (nebo zařadit do výroby)
//   diff = 0 → sync
function ActionLabel({ diff, shoptetQty }: { diff: number; shoptetQty: number | null }) {
  if (diff === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-success text-sm font-medium">
        <CheckIcon />
        OK
      </span>
    );
  }
  if (diff < 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-success text-sm font-semibold">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-success-light text-success text-xs leading-none">
          +
        </span>
        Na Shoptet přidat <strong>{Math.abs(diff)}</strong>&nbsp;ks
      </span>
    );
  }
  // diff > 0: Shoptet ukazuje víc než realita.
  // Speciální case: Shoptet = 0 a stále diff > 0 → nárok je vyšší než
  // (sklad + výroba). Odebírat ze Shoptetu nelze (už je 0) — zařadit do výroby.
  if (shoptetQty === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-50 text-primary text-xs leading-none">
          ▶
        </span>
        Zařadit do výroby <strong>{diff}</strong>&nbsp;ks
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-danger text-sm font-semibold">
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-danger-light text-danger text-xs leading-none">
        −
      </span>
      Ze Shoptetu odebrat <strong>{diff}</strong>&nbsp;ks
    </span>
  );
}

// Akce + tlačítko „Synchronizovat" — zapíše korekci stavu do OSTRÉHO Shoptetu
// (jen u akcí „přidat / odebrat"). Po úspěchu se řádek označí „Synchronizováno".
function ActionCell({
  item,
  snapshotId,
  onSynced,
}: {
  item: SnapshotItem;
  snapshotId: string;
  onSynced: () => void;
}) {
  const diff = item.diff;
  const excluded = !!item.is_excluded;
  const shoptetQty = item.shoptet_qty;
  const synced = !!item.synced_at;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (excluded) {
    return <span className="text-text-muted text-sm">—</span>;
  }

  if (synced) {
    return (
      <span className="inline-flex items-center gap-1.5 text-success text-sm font-medium">
        <CheckIcon />
        Synchronizováno
        <span className="text-text-muted">
          ({diff < 0 ? '+' : '−'}
          {Math.abs(diff)}&nbsp;ks)
        </span>
      </span>
    );
  }

  // Srovnat přes Shoptet lze jen „přidat" (diff<0) a „odebrat" (diff>0 & shoptet>0).
  const syncable = diff < 0 || (diff > 0 && (shoptetQty ?? 0) > 0);

  async function doSync() {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/snapshots/${snapshotId}/items/${item.id}/sync`, { method: 'POST' });
      onSynced(); // reload — řádek se přepne na „Synchronizováno"
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Zápis selhal');
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <ActionLabel diff={diff} shoptetQty={shoptetQty} />
      {syncable && (
        <span className="flex shrink-0 flex-col items-end gap-0.5">
          <button
            type="button"
            onClick={doSync}
            disabled={busy}
            className="cursor-pointer rounded-lg border border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Zapisuji…' : 'Synchronizovat'}
          </button>
          {err && <span className="max-w-[170px] text-right text-[10px] text-danger">{err}</span>}
        </span>
      )}
    </div>
  );
}
